import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../../shared/lib/apiClient';

const EDITABLE_FIELDS = [
  { key: 'display_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'company_name', label: 'Company' },
  { key: 'address_line1', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
];

const VendorReviewBanner = ({ loaded }) => {
  const [vendors, setVendors] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null); // vendor id
  const [editForm, setEditForm] = useState({});
  const [acting, setActing] = useState(null);

  useEffect(() => {
    fetchReviewVendors();
  }, []);

  const fetchReviewVendors = async () => {
    try {
      const res = await apiFetch('/api/vendors/review');
      if (res.ok) {
        const data = await res.json();
        setVendors(data.vendors || []);
      }
    } catch {
      // silent
    }
  };

  const handleApprove = async (vendorId) => {
    setActing(vendorId);
    try {
      const res = await apiFetch(`/api/vendors/${vendorId}/approve`, { method: 'PUT' });
      if (res.ok) {
        setVendors((prev) => prev.filter((v) => v.id !== vendorId));
        setExpanded(null);
        setEditing(null);
      }
    } catch {
      // silent
    } finally {
      setActing(null);
    }
  };

  const handleDelete = async (vendorId) => {
    setActing(vendorId);
    try {
      const res = await apiFetch(`/api/vendors/${vendorId}`, { method: 'DELETE' });
      if (res.ok) {
        setVendors((prev) => prev.filter((v) => v.id !== vendorId));
        setExpanded(null);
        setEditing(null);
      }
    } catch {
      // silent
    } finally {
      setActing(null);
    }
  };

  const startEditing = (vendor) => {
    setEditing(vendor.id);
    const form = {};
    EDITABLE_FIELDS.forEach((f) => {
      form[f.key] = vendor[f.key] || '';
    });
    setEditForm(form);
  };

  const cancelEditing = () => {
    setEditing(null);
    setEditForm({});
  };

  const handleSaveEdit = async (vendorId) => {
    setActing(vendorId);
    try {
      const res = await apiFetch(`/api/vendors/${vendorId}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        const data = await res.json();
        setVendors((prev) =>
          prev.map((v) => (v.id === vendorId ? { ...v, ...data.vendor } : v))
        );
        setEditing(null);
        setEditForm({});
      }
    } catch {
      // silent
    } finally {
      setActing(null);
    }
  };

  if (vendors.length === 0) return null;

  return (
    <div
      className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(12px)',
        transitionDelay: '250ms',
      }}
    >
      <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
        {/* Header — clickable to expand/collapse */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-amber-100/30 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-amber-600" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 1L1 8l7 7 7-7-7-7z" />
              <path d="M8 5v3" />
              <circle cx="8" cy="10.5" r="0.5" fill="currentColor" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">
              {vendors.length} new vendor{vendors.length !== 1 ? 's' : ''} detected from invoices
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              Review and confirm to keep them in your vendor list, or{' '}
              <Link to="/dashboard/vendors" className="underline hover:text-amber-800 transition-colors" onClick={(e) => e.stopPropagation()}>
                manage all vendors
              </Link>
            </p>
          </div>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className={`text-amber-400 shrink-0 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
          >
            <path d="M3.5 5.25L7 8.75L10.5 5.25" />
          </svg>
        </button>

        {/* Vendor list */}
        {!collapsed && <div className="border-t border-amber-200">
          {vendors.map((vendor, i) => (
            <div key={vendor.id} className={i > 0 ? 'border-t border-amber-100' : ''}>
              {/* Vendor row */}
              <button
                onClick={() => {
                  setExpanded(expanded === vendor.id ? null : vendor.id);
                  if (editing && editing !== vendor.id) cancelEditing();
                }}
                className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-amber-100/50 transition-colors text-left"
              >
                <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-medium text-amber-700">
                    {vendor.display_name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <span className="text-sm text-amber-900 font-medium truncate flex-1">
                  {vendor.display_name}
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className={`text-amber-400 transition-transform duration-200 ${expanded === vendor.id ? 'rotate-180' : ''}`}
                >
                  <path d="M3 4.5L6 7.5L9 4.5" />
                </svg>
              </button>

              {/* Expanded details + actions */}
              {expanded === vendor.id && (
                <div className="px-4 pb-3 pt-1">
                  {editing === vendor.id ? (
                    /* Edit mode */
                    <div className="bg-white rounded-md border border-amber-100 p-3 mb-3">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {EDITABLE_FIELDS.map((field) => (
                          <div key={field.key}>
                            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                              {field.label}
                            </label>
                            <input
                              type="text"
                              value={editForm[field.key] || ''}
                              onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                              }
                              className="w-full px-2 py-1.5 text-xs border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-900 text-zinc-800"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="rounded-md border border-zinc-900 overflow-hidden mb-3">
                      <div className="grid grid-cols-3 divide-x divide-zinc-900">
                        <GridCell label="Name" value={vendor.display_name} />
                        <GridCell label="Email" value={vendor.email} />
                        <GridCell label="Phone" value={vendor.phone} />
                      </div>
                      <div className="grid grid-cols-3 divide-x divide-zinc-900 border-t border-zinc-900">
                        <GridCell label="Company" value={vendor.company_name} />
                        <GridCell label="Address" value={vendor.address_line1} />
                        <GridCell label="Location" value={[vendor.city, vendor.state, vendor.zip].filter(Boolean).join(', ')} />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {editing === vendor.id ? (
                      <>
                        <button
                          onClick={() => handleSaveEdit(vendor.id)}
                          disabled={acting === vendor.id}
                          className="px-3 py-1.5 bg-zinc-900 text-white text-[10px] uppercase tracking-[0.15em] rounded hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        >
                          {acting === vendor.id ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          disabled={acting === vendor.id}
                          className="px-3 py-1.5 border border-zinc-200 text-zinc-600 text-[10px] uppercase tracking-[0.15em] rounded hover:bg-zinc-50 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(vendor.id)}
                          disabled={acting === vendor.id}
                          className="px-3 py-1.5 bg-zinc-900 text-white text-[10px] uppercase tracking-[0.15em] rounded hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        >
                          {acting === vendor.id ? 'Saving...' : 'Keep Vendor'}
                        </button>
                        <button
                          onClick={() => startEditing(vendor)}
                          disabled={acting === vendor.id}
                          className="px-3 py-1.5 border border-zinc-200 text-zinc-600 text-[10px] uppercase tracking-[0.15em] rounded hover:bg-zinc-50 transition-colors disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(vendor.id)}
                          disabled={acting === vendor.id}
                          className="px-3 py-1.5 border border-red-200 text-red-600 text-[10px] uppercase tracking-[0.15em] rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>}
      </div>
    </div>
  );
};

const GridCell = ({ label, value }) => (
  <div className="bg-white px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">{label}</div>
    <div className="text-xs text-zinc-800 font-medium break-words">{value || '—'}</div>
  </div>
);

export default VendorReviewBanner;
